import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {

  Box,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  IconButton,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Tooltip,
  Snackbar,
  Alert,
  Menu,
  MenuItem
} from '@mui/material';
import { Lock, Public } from '@mui/icons-material';
import { EyeIcon, DotsThreeIcon, CheckIcon, MinusIcon, PencilSimpleIcon, TrashIcon, PencilLineIcon, ListPlusIcon } from '@phosphor-icons/react';
import { tmdbService, TMDbMovie } from '../../services/tmdbApi';
// Removed deprecated getMovieReview import
import { User } from 'firebase/auth';

import CustomButton from './CustomButton';
import { useUserMovieData } from '../../contexts/UserMovieDataContext';
import { useCommonToasts } from '../../hooks/useCommonToasts';
import { usePerformanceMonitor } from '../../hooks/usePerformanceMonitor';
import MarkAsWatchedDialog from '../dialogs/MarkAsWatchedDialog';
import WatchlistMultiSelectDialog from '../dialogs/WatchlistMultiSelectDialog';
import MovieNoteDialog from '../dialogs/MovieNoteDialog';
import { Timestamp } from 'firebase/firestore';
import { getStandardizedMovieId } from '../../utils/movieUtils';
import {
  getStatusBadgeStyles,
  getMovieCardStyles,
  getMovieCardPosterStyles,
  getMovieCardContentStyles,
  getMovieCardStatusBadgeStyles
} from '../../tokens/componentTokens';
import { useUserWatchlistData } from '../../contexts/UserWatchlistDataContext';
import ImageShimmer from './ImageShimmer';
import { movieService } from '@watchlist/shared';
import { useMovieRatingsOptional } from '../../contexts/MovieRatingContext';
import CollaboratorsWatchedDialog from '../dialogs/CollaboratorsWatchedDialog';
import { CollaboratorProfile } from '../../types/watchlist';
import { useHighlightOptional } from '../../contexts/HighlightContext';
import { keyframes } from '@mui/system';
import { font } from '../../utils/fontHelper';
import { parseFirebaseDate, formatFirebaseDate, formatWatchedDate } from '../../utils/dateUtils';
import { relativeTime } from '../../utils/crewFeedUtils';
import { preloadImageUrlDebounced } from '../../utils/imagePreload';
import { prefetchMovieDetailClient } from '../../utils/prefetchMovieForDetail';
import { stashMovieDetailNavPreviewFromMovie } from '../../utils/movieDetailNavPreview';
import { glassStyles } from '../../tokens/designTokens';

// Highlight pulse animation
const highlightPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(88, 86, 214, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 20px 10px rgba(88, 86, 214, 0.3);
    transform: scale(1.02);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(88, 86, 214, 0);
    transform: scale(1);
  }
`;

interface StandardMovieCardProps {
  movie: TMDbMovie;
  onClick: () => void;
  onToggleWatchlist?: () => void;
  onToggleWatched?: (
    rating?: number,
    review?: string,
    isPrivate?: boolean,
    visibility?: 'anyone' | 'only-me',
    watchedDate?: Date | null,
    watchedDatePrecision?: 'day' | 'month' | 'year'
  ) => void;
  isInWatchlist?: boolean;
  isWatched?: boolean;
  showStatusBadge?: boolean;
  variant?: 'list' | 'grid' | 'poster-only' | 'watched-review' | 'portrait';
  size?: 'small' | 'medium' | 'large';
  addedOn?: string;
  watchedOn?: string;
  user?: User;
  showContextMenu?: boolean; // Show 3-dot menu only in watchlist contexts
  currentWatchlistId?: string; // ID of current watchlist for removal functionality
  onRemoveFromWatchlist?: () => void; // Callback for removing from current watchlist
  movieNote?: string; // Current note for the movie
  onUpdateNote?: (note: string) => void; // Callback for updating movie note
  addedByName?: string; // Name of user who added the movie in collaboration lists

  // New props for collaboration context
  isCollaborativeList?: boolean; // Whether this is a collaborative list
  isPublicList?: boolean; // Whether this collaborative list is public
  currentUserIsCollaborator?: boolean; // Whether current user is a collaborator of this list

  // Collaborative watched status
  watchedBy?: string[];
  collaborators?: CollaboratorProfile[];

  // Search match information
  matchedFields?: string[]; // Fields that matched the search query
  relevanceScore?: number; // Search relevance score
}

// Add React.memo with custom comparison function
const StandardMovieCard: React.FC<StandardMovieCardProps> = React.memo(({
  movie,
  onClick,
  onToggleWatchlist,
  onToggleWatched,
  isInWatchlist = false,
  isWatched = false,
  showStatusBadge = true,
  variant = 'list',
  size = 'medium',
  addedOn,
  watchedOn,
  user,
  showContextMenu = false,
  currentWatchlistId,
  onRemoveFromWatchlist,
  movieNote,
  onUpdateNote,
  addedByName,
  isCollaborativeList = false,
  isPublicList = false,
  currentUserIsCollaborator = false,
  matchedFields,
  relevanceScore,
  watchedBy,
  collaborators,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  usePerformanceMonitor('StandardMovieCard');

  // Highlight context for scroll-to and highlight animation
  const highlightContext = useHighlightOptional();

  const [showCollaboratorsWatchedDialog, setShowCollaboratorsWatchedDialog] = useState(false);

  // Helper function to format matched fields into user-friendly text
  const getMatchReasonText = (fields: string[]): string => {
    if (!fields || fields.length === 0) return '';

    const fieldMap: { [key: string]: string } = {
      'title': 'Title match',
      'overview': 'Plot match',
      'genre': 'Genre match',
      'language': 'Language match',
      'original_language': 'Original language',
      'title_similarity': 'Similar title',
      'combined_search': 'Multi-word match',
      'multi_keyword': 'Multiple keywords',
      'cast': 'Cast member',
      'crew': 'Crew member',
      'director': 'Director match',
      'actor': 'Actor match',
      'production': 'Production company',
      'keyword': 'Keyword match',
      'plot': 'Plot keyword',
      'person_connection': 'Person connection',
      'cast_crew': 'Cast/Crew',
      'discover': 'Discovery match',
      'filtered_search': 'Filtered search'
    };

    const reasons = fields.map(field => {
      // Handle dynamic fields like matches_2_terms
      if (field.startsWith('matches_') && field.endsWith('_terms')) {
        const count = field.match(/matches_(\d+)_terms/)?.[1];
        return count ? `${count} keyword matches` : fieldMap[field];
      }

      // Handle crew role fields like crew_cinematographer
      if (field.startsWith('crew_')) {
        const role = field.replace('crew_', '').replace('_', ' ');
        return `${role.charAt(0).toUpperCase() + role.slice(1)} match`;
      }

      return fieldMap[field] || field.replace('_', ' ').toLowerCase();
    }).filter(Boolean);

    if (reasons.length === 0) return '';
    if (reasons.length === 1) return reasons[0];
    if (reasons.length === 2) return `${reasons[0]} & ${reasons[1]}`;

    return `${reasons.slice(0, -1).join(', ')} & ${reasons[reasons.length - 1]}`;
  };
  const [showWatchlistRemoveDialog, setShowWatchlistRemoveDialog] = useState(false);
  const [showUnwatchDialog, setShowUnwatchDialog] = useState(false);
  const [showMarkWatchedDialog, setShowMarkWatchedDialog] = useState(false);
  const [showWatchlistMultiSelectDialog, setShowWatchlistMultiSelectDialog] = useState(false);
  const [showMovieNoteDialog, setShowMovieNoteDialog] = useState(false);
  const [showRemoveNoteDialog, setShowRemoveNoteDialog] = useState(false);
  const [averageUserRating, setAverageUserRating] = useState<number | null>(null);
  const [isOperationInProgress, setIsOperationInProgress] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Get user movie data from context for accurate review and date information
  const { getUserMovieData, refreshMovieData, addToWatchlist, removeFromWatchlist, markAsWatched, unmarkAsWatched, cacheVersion, deleteRatingAndReview } = useUserMovieData();
  const { userWatchlists } = useUserWatchlistData();

  // Always use standardized movie ID to prevent duplicates
  const movieId = useMemo(() => {
    const standardizedId = getStandardizedMovieId(movie);
    console.log(`StandardMovieCard [${movie.Title}]: Using standardized ID:`, {
      originalMovie: movie,
      standardizedId,
      hasImdbID: !!movie.imdbID,
      hasTmdbId: !!movie.id
    });
    return standardizedId;
  }, [movie]);

  // Check if this movie card is highlighted (for scroll-to-highlight feature)
  const isHighlighted = highlightContext?.isHighlighted(movieId, 'movie') ?? false;

  /** Desktop: pointer hover. Capacitor / touch: touchstart (no reliable mouseenter before tap). */
  const handlePosterPreload = useCallback(() => {
    if (!movie.Poster || movie.Poster === 'N/A') return;
    preloadImageUrlDebounced(tmdbService.getGridPoster(movie.Poster));
  }, [movie.Poster]);

  const handleOpenMovieClick = useCallback(() => {
    stashMovieDetailNavPreviewFromMovie(movieId, movie, () => {
      if (movie.poster_path && movie.poster_path !== 'N/A') {
        if (movie.poster_path.startsWith('http')) return movie.poster_path;
        return tmdbService.getHighQualityPoster(movie.poster_path);
      }
      if (movie.Poster && movie.Poster !== 'N/A') {
        return tmdbService.getHighQualityPoster(movie.Poster);
      }
      return null;
    });
    prefetchMovieDetailClient(movieId);
    onClick();
  }, [movieId, movie, onClick]);

  // 🚨 COMPREHENSIVE FIX: Get user movie data from BOTH contexts
  const contextDataFromUserMovieData = getUserMovieData(movieId);

  // Check if movie is in ANY custom watchlist
  const isInAnyCustomWatchlist = useMemo(() => {
    if (!movieId || movieId === 'unknown') return false;

    return userWatchlists.some(watchlist => {
      return watchlist.movieDetails.some(movie => {
        const movieStandardizedId = getStandardizedMovieId(movie);
        return movieStandardizedId === movieId ||
          movie.id === movieId ||
          movie.imdbID === movieId;
      });
    });
  }, [userWatchlists, movieId]);

  // 🚀 NEW: Check if movie is in collaboration lists added by current user
  const isInCollabWatchlistsByUser = useMemo(() => {
    if (!movieId || movieId === 'unknown' || !user?.uid) return false;

    return userWatchlists.some(watchlist => {
      // Skip non-collaborative lists
      if (!watchlist.collaborators || watchlist.collaborators.length === 0) return false;

      return watchlist.movieDetails.some(movie => {
        const movieStandardizedId = getStandardizedMovieId(movie);
        const isMovieMatch = movieStandardizedId === movieId ||
          movie.id === movieId ||
          movie.imdbID === movieId;

        // Check if current user added this movie
        const isAddedByCurrentUser = movie.addedBy === user.uid;

        return isMovieMatch && isAddedByCurrentUser;
      });
    });
  }, [userWatchlists, movieId, user?.uid]);

  // 🚀 NEW: Check if movie is in collaboration lists but only added by others
  const isInCollabWatchlistsByOthers = useMemo(() => {
    if (!movieId || movieId === 'unknown' || !user?.uid) return false;

    return userWatchlists.some(watchlist => {
      // Skip non-collaborative lists
      if (!watchlist.collaborators || watchlist.collaborators.length === 0) return false;

      return watchlist.movieDetails.some(movie => {
        const movieStandardizedId = getStandardizedMovieId(movie);
        const isMovieMatch = movieStandardizedId === movieId ||
          movie.id === movieId ||
          movie.imdbID === movieId;

        // Check if movie was added by someone else
        const isAddedByOtherUser = movie.addedBy && movie.addedBy !== user.uid;

        return isMovieMatch && isAddedByOtherUser;
      });
    });
  }, [userWatchlists, movieId, user?.uid]);

  // Combine both data sources for comprehensive movie status
  const contextData = useMemo(() => {
    const baseData = contextDataFromUserMovieData;

    // 🚀 UPDATED: If movie is in any watchlist added by current user, it should be considered "in watchlist"
    if (isInCollabWatchlistsByUser) {
      return {
        ...baseData,
        isInWatchlist: true, // Override to true if in any watchlist added by current user
        movieId: movieId,
        uid: baseData?.uid || user?.uid || '',
        isWatched: baseData?.isWatched || false,
        isFavorite: baseData?.isFavorite || false,
        watchCount: baseData?.watchCount || 0,
        createdAt: baseData?.createdAt || new Date() as any,
        updatedAt: baseData?.updatedAt || new Date() as any,
      };
    }

    return baseData;
  }, [contextDataFromUserMovieData, isInCollabWatchlistsByUser, movieId, user?.uid]);

  // ALWAYS use context data over props for consistency with MovieDetailPage
  // Check both personal watchlist AND custom watchlists for comprehensive status
  // 🚀 UPDATED: Only consider movies added by current user as "in watchlist"
  const actualIsInWatchlist = useMemo(() => {
    // Check personal "Want to Watch" list
    if (contextData?.isInWatchlist) return true;

    // Check if movie is in watchlists added by current user (not by collaborators)
    return isInCollabWatchlistsByUser;
  }, [contextData?.isInWatchlist, isInCollabWatchlistsByUser]);

  const actualIsWatched = contextData?.isWatched || false;

  // Helper function to determine if we should show "Added by" information
  const shouldShowAddedBy = useMemo(() => {
    // Only show in collaborative lists with addedByName
    if (!isCollaborativeList || !addedByName) {
      return false;
    }

    // ACCESS CONTROL: Only show "added by" to collaborators (including the list owner)
    if (!currentUserIsCollaborator) {
      return false;
    }

    // Show "added by" in all collaborative lists for collaborators
    return true;
  }, [isCollaborativeList, addedByName, currentUserIsCollaborator, movie.Title]);

  // Debug: Check for inconsistencies between props and context (only log warnings)
  useEffect(() => {
    if (movieId && movieId !== 'unknown') {
      const inconsistentWatchlist = isInWatchlist !== actualIsInWatchlist;
      const inconsistentWatched = isWatched !== actualIsWatched;

      // Only warn if props are explicitly provided and different from context
      // Don't warn if props are undefined (default false) - this is expected in search contexts
      const shouldWarnWatchlist = inconsistentWatchlist && isInWatchlist !== undefined;
      const shouldWarnWatched = inconsistentWatched && isWatched !== undefined;

      if (shouldWarnWatchlist || shouldWarnWatched) {
        console.warn(`StandardMovieCard [${movie.Title}]: Props vs Context inconsistency:`, {
          movieId,
          watchlistMismatch: shouldWarnWatchlist,
          watchedMismatch: shouldWarnWatched,
          propsProvided: { isInWatchlist, isWatched },
          contextData: { actualIsInWatchlist, actualIsWatched }
        });
      }
    }
  }, [movieId, isInWatchlist, isWatched, actualIsInWatchlist, actualIsWatched, movie.Title]);

  const toasts = useCommonToasts();

  // Removed excessive debug logging to improve performance

  // Force re-evaluation when cache version changes (new data available)
  // Reduced dependency on cacheVersion to prevent excessive re-renders
  useEffect(() => {
    if (movieId && movieId !== 'unknown' && contextData) {
      // Only trigger refresh if we actually have context data changes
      setRefreshTrigger(prev => prev + 1);
    }
  }, [movieId, contextData?.isInWatchlist, contextData?.isWatched, contextData?.rating, contextData?.reviewVisibility]);

  // Use batch rating context for efficient rating fetching
  // Falls back to individual fetch if context is not available
  const movieRatings = useMovieRatingsOptional();
  const cachedRating = movieRatings?.getRating(movieId);

  // Prefetch rating via context when mounted (context handles batching and caching)
  useEffect(() => {
    if (movieId && movieId !== 'unknown' && movieRatings) {
      movieRatings.prefetchRatings([movieId]);
    }
  }, [movieId, movieRatings]);

  // Update local state when cached rating becomes available
  useEffect(() => {
    if (cachedRating !== null && cachedRating !== undefined) {
      setAverageUserRating(cachedRating);
    }
  }, [cachedRating]);

  // Fallback: Fetch individual rating if context is not available
  useEffect(() => {
    // Skip if context is available (it handles fetching)
    if (movieRatings) return;

    const fetchAverageRating = async () => {
      try {
        const ratings = await movieService.getMovieRatings(movieId);
        if (ratings.length > 1) { // Match MovieDetail's threshold for consistency
          const sum = ratings.reduce((acc, rating) => acc + rating, 0);
          const average = sum / ratings.length;
          setAverageUserRating(Math.round(average * 10) / 10); // Round to 1 decimal place
        } else {
          setAverageUserRating(null); // Clear if not enough ratings
        }
      } catch (error) {
        // Check if this is a Firestore index error
        if (error instanceof Error && error.message && error.message.includes('requires an index')) {
          // Silently skip rating fetch if indexes are missing - this is expected during deployment
          console.log('⚠️ Firestore indexes not yet deployed - skipping rating fetch for movie:', movieId);
        } else {
          console.error('Error fetching movie ratings:', error);
        }
        setAverageUserRating(null); // Clear on error for consistency
      }
    };

    if (movieId && movieId !== 'unknown') {
      fetchAverageRating();
    }
  }, [movieId, movieRatings]); // Only fetch individually if context not available

  // Get user review for this movie - use context data if available
  const userReview = contextData && (contextData.rating || contextData.review || contextData.isWatched) ? (() => {
    const toDate = parseFirebaseDate;

    const resolvedWatchedDate =
      toDate(contextData.watchedDate) ||
      toDate((contextData as any).lastWatchedDate);

    const resolvedUpdatedDate =
      toDate(contextData.updatedAt) ||
      toDate((contextData as any).lastUpdated) ||
      toDate(contextData.createdAt) ||
      resolvedWatchedDate;

    const watchedDateLabel = formatWatchedDate(
      contextData.watchedDate || (contextData as any).lastWatchedDate,
      contextData.watchedDatePrecision
    ) || undefined;

    const watchedDateIso = resolvedWatchedDate
      ? resolvedWatchedDate.toISOString()
      : undefined;

    const updatedDateLabel = formatFirebaseDate(
      resolvedUpdatedDate,
      watchedDateLabel || 'Recently',
      { year: 'numeric', month: 'long', day: 'numeric' }
    );

    const updatedDateIso = resolvedUpdatedDate
      ? resolvedUpdatedDate.toISOString()
      : watchedDateIso;

    return {
      id: 'user-review',
      userId: user?.uid || 'anonymous',
      userName: user?.displayName || 'You',
      userAvatar: user?.photoURL || '',
      rating: contextData.rating,
      review: contextData.review || '',
      visibility: contextData.reviewVisibility, // Add visibility for privacy icons
      date: updatedDateLabel,
      watchedDate: watchedDateIso,
      watchedDateLabel,
      watchedDatePrecision: contextData.watchedDatePrecision,
      updatedDate: updatedDateIso,
      updatedDateLabel
    };
  })() : null; // Remove deprecated getMovieReview call

  const userReviewUpdatedLabel = userReview
    ? (() => {
        const d = parseFirebaseDate(userReview.updatedDate || userReview.date);
        return d ? relativeTime(d.getTime()) : 'recently';
      })()
    : undefined;
  const userReviewWatchedLabel = userReview
    ? formatWatchedDate(userReview.watchedDate || userReview.watchedDateLabel, userReview.watchedDatePrecision)
    : undefined;

  // Auto-detect variant based on review availability
  const effectiveVariant = userReview && actualIsWatched ? 'watched-review' : variant;

  // Size configurations
  const sizeConfig = {
    small: { width: 56, height: 64, fontSize: 12 },
    medium: { width: 70, height: 76, fontSize: 14 },
    large: { width: 84, height: 96, fontSize: 16 },
  };

  const posterSize = sizeConfig[size];

  const handleWatchlistToggle = async () => {
    if (isOperationInProgress) {
      console.log(`StandardMovieCard [${movie.Title}]: Operation already in progress, skipping`);
      return;
    }

    if (!actualIsInWatchlist && !showWatchlistRemoveDialog) {
      // Add to watchlist and show toast with undo and clickable watchlist name
      setIsOperationInProgress(true);
      try {
        const movieTitle = movie.Title || movie.title || 'Unknown Movie';
        const moviePoster = movie.Poster !== 'N/A' ? movie.Poster : undefined;
        const movieYear = String(movie.Year || movie.year || 'Unknown');

        console.log(`StandardMovieCard [${movieTitle}]: Adding to watchlist`);
        await addToWatchlist(movieId, movieTitle, moviePoster, movieYear);
        await refreshMovieData(movieId);

        // Show toast with undo and clickable watchlist name
        toasts.movieAddedToWatchlist(
          movieTitle,
          "Want to Watch",
          // Undo action
          async () => {
            console.log(`StandardMovieCard [${movieTitle}]: Undoing add to watchlist`);
            await removeFromWatchlist(movieId);
            await refreshMovieData(movieId);
          },
          // Clickable watchlist name action
          () => {
            setShowWatchlistMultiSelectDialog(true);
          }
        );

        if (onToggleWatchlist) {
          onToggleWatchlist();
        }
      } catch (error) {
        console.error(`StandardMovieCard [${movie.Title}]: Error adding movie to watchlist:`, error);
      } finally {
        setIsOperationInProgress(false);
      }
    } else {
      // Show confirmation dialog for removal
      setShowWatchlistRemoveDialog(true);
    }
  };

  const handleWatchedToggle = () => {
    if (actualIsWatched && !showUnwatchDialog) {
      setShowUnwatchDialog(true);
    } else if (!actualIsWatched) {
      // Open mark as watched dialog
      setShowMarkWatchedDialog(true);
    }
  };

  const handleWatchlistBadgeClick = async () => {
    if (variant === 'watched-review') {
      setShowWatchlistRemoveDialog(true);
    } else {
      // Determine which watchlists contain this movie
      const watchlistsContainingMovie = getWatchlistsContainingMovie();

      console.log(`StandardMovieCard [${movie.Title}]: Movie is in ${watchlistsContainingMovie.length} watchlists:`, watchlistsContainingMovie);

      if (watchlistsContainingMovie.length === 0) {
        // 🚀 NEW BEHAVIOR: Movie is NOT in any watchlists - add directly to "Want to Watch" + show toast with undo
        console.log(`StandardMovieCard [${movie.Title}]: Movie not in any watchlists, adding directly to Want to Watch`);

        if (isOperationInProgress) {
          console.log(`StandardMovieCard [${movie.Title}]: Operation already in progress, skipping`);
          return;
        }

        setIsOperationInProgress(true);
        try {
          const movieTitle = movie.Title || movie.title || 'Unknown Movie';
          const moviePoster = movie.Poster !== 'N/A' ? movie.Poster : undefined;
          const movieYear = String(movie.Year || movie.year || 'Unknown');

          console.log(`StandardMovieCard [${movieTitle}]: Adding to Want to Watch via badge click`);
          await addToWatchlist(movieId, movieTitle, moviePoster, movieYear);
          await refreshMovieData(movieId);

          // Show toast with undo and option to manage watchlists
          toasts.movieAddedToWatchlist(
            movieTitle,
            "Want to Watch",
            // Undo action
            async () => {
              console.log(`StandardMovieCard [${movieTitle}]: Undoing add to watchlist`);
              await removeFromWatchlist(movieId);
              await refreshMovieData(movieId);
            },
            // Manage watchlists action (opens multi-select dialog)
            () => {
              console.log(`StandardMovieCard [${movieTitle}]: Opening multi-select dialog from toast`);
              setShowWatchlistMultiSelectDialog(true);
            }
          );

          if (onToggleWatchlist) {
            onToggleWatchlist();
          }
        } catch (error) {
          console.error(`StandardMovieCard [${movie.Title}]: Error adding movie to watchlist:`, error);
          toasts.permissionError();
        } finally {
          setIsOperationInProgress(false);
        }
      } else {
        // 🚀 UPDATED: Movie is in any watchlist(s) - show multi-select dialog to manage watchlists
        console.log(`StandardMovieCard [${movie.Title}]: Movie already in ${watchlistsContainingMovie.length} watchlist(s), opening multi-select dialog`);
        setShowWatchlistMultiSelectDialog(true);
      }
    }
  };

  // Helper function to get all watchlists containing this movie
  const getWatchlistsContainingMovie = () => {
    const standardizedMovieId = getStandardizedMovieId({ id: movieId, imdbID: movieId });
    const movieData = getUserMovieData(standardizedMovieId);
    const watchlists: string[] = [];

    console.log('StandardMovieCard: Checking watchlists for movie:', {
      originalMovieId: movieId,
      standardizedMovieId,
      movieData: movieData ? 'found' : 'not found'
    });

    // Check if movie is in personal "Want to Watch" list
    if (movieData?.isInWatchlist) {
      watchlists.push('personal-watchlist');
      console.log('StandardMovieCard: Found in personal watchlist');
    }

    // 🚀 UPDATED: Check which user-created watchlists contain this movie added by current user
    userWatchlists.forEach(watchlist => {
      const hasMovieAddedByUser = watchlist.movieDetails.some(movie => {
        const watchlistMovieId = getStandardizedMovieId(movie);
        const isMatch = watchlistMovieId === standardizedMovieId;

        // 🚀 NEW: Only count movies added by current user
        const isAddedByCurrentUser = movie.addedBy === user?.uid;

        if (isMatch && isAddedByCurrentUser) {
          console.log('StandardMovieCard: Found match in watchlist added by current user:', watchlist.name, {
            watchlistMovieId,
            standardizedMovieId,
            movieTitle: movie.title,
            addedBy: movie.addedBy,
            currentUser: user?.uid
          });
        }

        return isMatch && isAddedByCurrentUser;
      });

      if (hasMovieAddedByUser) {
        watchlists.push(watchlist.id);
      }
    });

    console.log('StandardMovieCard: Movie found in watchlists (added by current user):', watchlists);
    return watchlists;
  };

  const confirmRemoveFromWatchlist = async () => {
    if (isOperationInProgress) {
      console.log(`StandardMovieCard [${movie.Title}]: Operation already in progress, skipping removal`);
      return;
    }

    setIsOperationInProgress(true);

    try {
      console.log(`StandardMovieCard [${movie.Title}]: Removing from watchlist`);

      // Close dialog immediately to prevent multiple clicks
      setShowWatchlistRemoveDialog(false);

      // Determine which watchlists contain this movie
      const watchlistsContainingMovie = getWatchlistsContainingMovie();
      console.log(`StandardMovieCard [${movie.Title}]: Movie is in ${watchlistsContainingMovie.length} watchlists:`, watchlistsContainingMovie);

      const title = movie.Title || movie.title || 'Movie';
      const movieYear = String(movie.Year || movie.year || 'Unknown');
      const poster = movie.Poster !== 'N/A' ? movie.Poster : undefined;

      if (watchlistsContainingMovie.length <= 1) {
        // Movie is only in one watchlist (or personal watchlist only) - remove from all
        console.log(`StandardMovieCard [${movie.Title}]: Removing from all watchlists`);
        await removeFromWatchlist(movieId);
        await refreshMovieData(movieId);
        toasts.movieRemovedFromWatchlist(title, 'Want to Watch', async () => {
          await addToWatchlist(movieId, title, poster, movieYear);
          await refreshMovieData(movieId);
        });
      } else {
        // Movie is in multiple watchlists - remove only from personal watchlist
        console.log(`StandardMovieCard [${movie.Title}]: Removing only from personal watchlist (keeping in custom watchlists)`);
        const movieData = getUserMovieData(movieId);

        // Only remove from personal watchlist if it's there
        if (movieData?.isInWatchlist) {
          await removeFromWatchlist(movieId);
          await refreshMovieData(movieId);
          toasts.movieRemovedFromWatchlist(title, 'Want to Watch', async () => {
            await addToWatchlist(movieId, title, poster, movieYear);
            await refreshMovieData(movieId);
          });
        } else {
          console.log(`StandardMovieCard [${movie.Title}]: Movie not in personal watchlist, nothing to remove`);
        }
      }

      if (onToggleWatchlist) {
        onToggleWatchlist();
      }
    } catch (error) {
      console.error(`StandardMovieCard [${movie.Title}]: Error removing movie from watchlist:`, error);
      // Show error and reopen dialog on failure
      setShowWatchlistRemoveDialog(true);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  const confirmUnwatch = async () => {
    if (isOperationInProgress) {
      console.log(`StandardMovieCard [${movie.Title}]: Operation already in progress, skipping unwatch`);
      return;
    }

    setIsOperationInProgress(true);

    try {
      console.log(`StandardMovieCard [${movie.Title}]: Removing from watched list (will delete rating/review and unwatch)`);

      // Close dialog immediately to prevent multiple clicks
      setShowUnwatchDialog(false);

      // 🚀 FIX: Use deleteRatingAndReview instead of unmarkAsWatched for business rule consistency
      // When removing from "watched" or "your-reviews" lists, we should remove the entire review/rating
      // This maintains the business rule: no movie can have review/rating without being watched
      await deleteRatingAndReview(movieId);

      // Force a state refresh after a short delay to ensure UI updates
      setTimeout(() => {
        setRefreshTrigger(prev => prev + 1);
      }, 300);

      toasts.movieUnmarkedAsWatched(movie.Title || movie.title || 'Movie');

      if (onToggleWatched) {
        onToggleWatched();
      }
    } catch (error) {
      console.error(`StandardMovieCard [${movie.Title}]: Error removing from watched list:`, error);
      // Show error and reopen dialog on failure
      setShowUnwatchDialog(true);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent triggering the card click
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleAddToAnotherList = () => {
    setMenuAnchorEl(null);
    setShowWatchlistMultiSelectDialog(true);
  };

  const handleRemoveFromList = () => {
    setMenuAnchorEl(null);

    // Check if we have a specific currentWatchlistId (custom watchlist)
    if (currentWatchlistId && currentWatchlistId !== 'want-to-watch' && currentWatchlistId !== 'watched' && currentWatchlistId !== 'your-reviews' && currentWatchlistId !== 'main-watchlist') {
      // This is a custom watchlist - always remove only from this specific list
      console.log(`StandardMovieCard [${movie.Title}]: Removing from custom watchlist: ${currentWatchlistId}`);
      if (onRemoveFromWatchlist) {
        onRemoveFromWatchlist();
      }
    } else {
      // This is a default watchlist - handle based on watchlist type
      console.log(`StandardMovieCard [${movie.Title}]: Removing from default watchlist: ${currentWatchlistId}`);

      if (currentWatchlistId === 'want-to-watch') {
        // For "want to watch" list: check if movie is in multiple lists
        const watchlistsContainingMovie = getWatchlistsContainingMovie();
        console.log(`StandardMovieCard [${movie.Title}]: Movie is in ${watchlistsContainingMovie.length} watchlists:`, watchlistsContainingMovie);

        if (watchlistsContainingMovie.length <= 1) {
          // Movie is only in one watchlist - remove from all
          console.log(`StandardMovieCard [${movie.Title}]: Removing from all watchlists`);
          setShowWatchlistRemoveDialog(true);
        } else {
          // Movie is in multiple watchlists - show multi-select dialog to let user choose
          console.log(`StandardMovieCard [${movie.Title}]: Opening multi-select dialog for removal`);
          setShowWatchlistMultiSelectDialog(true);
        }
      } else if (currentWatchlistId === 'watched' || currentWatchlistId === 'your-reviews') {
        // For "watched" and "your reviews" lists: show unwatch confirmation dialog
        console.log(`StandardMovieCard [${movie.Title}]: Opening unwatch confirmation dialog`);
        setShowUnwatchDialog(true);
      } else {
        // Fallback to original logic for other default watchlists
        const watchlistsContainingMovie = getWatchlistsContainingMovie();
        console.log(`StandardMovieCard [${movie.Title}]: Movie is in ${watchlistsContainingMovie.length} watchlists:`, watchlistsContainingMovie);

        if (watchlistsContainingMovie.length <= 1) {
          // Movie is only in one watchlist - remove from all
          console.log(`StandardMovieCard [${movie.Title}]: Removing from all watchlists`);
          setShowWatchlistRemoveDialog(true);
        } else {
          // Movie is in multiple watchlists - show multi-select dialog to let user choose
          console.log(`StandardMovieCard [${movie.Title}]: Opening multi-select dialog for removal`);
          setShowWatchlistMultiSelectDialog(true);
        }
      }
    }
  };

  const handleMarkAsWatched = () => {
    setMenuAnchorEl(null);
    // Open mark as watched dialog
    setShowMarkWatchedDialog(true);
  };

  const handleAddEditNote = () => {
    setMenuAnchorEl(null);
    setShowMovieNoteDialog(true);
  };

  const handleSaveNote = async (note: string) => {
    if (onUpdateNote) {
      try {
        await onUpdateNote(note);
      } catch (error) {
        console.error(`StandardMovieCard [${movie.Title}]: Error saving note:`, error);
        // Dialog will close but error is logged
      }
    }
    setShowMovieNoteDialog(false);
  };

  const handleRemoveNote = () => {
    setMenuAnchorEl(null);
    setShowRemoveNoteDialog(true);
  };

  const confirmRemoveNote = async () => {
    setShowRemoveNoteDialog(false);
    setIsOperationInProgress(true);

    try {
      if (onUpdateNote) {
        console.log(`StandardMovieCard [${movie.Title}]: Removing note`);
        await onUpdateNote(''); // Clear the note by setting it to empty string
        console.log(`StandardMovieCard [${movie.Title}]: Note removed successfully`);
      }
    } catch (error) {
      console.error(`StandardMovieCard [${movie.Title}]: Error removing note:`, error);
      // Show error and reopen dialog on failure
      setShowRemoveNoteDialog(true);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  // Get existing rating/review data for the dialog
  const existingRating = contextData?.rating || undefined;
  const existingReview = contextData?.review || undefined;
  const existingWatchedDate = contextData?.watchedDate ? parseFirebaseDate(contextData.watchedDate) : undefined;

  // Handle mark as watched submission from dialog
  const handleMarkAsWatchedSubmit = async (
    rating?: number,
    review?: string,
    isPrivate?: boolean,
    visibility?: 'anyone' | 'only-me',
    watchedDate?: Date | null,
    watchedDatePrecision?: 'day' | 'month' | 'year'
  ) => {
    setShowMarkWatchedDialog(false);
    setIsOperationInProgress(true);

    try {
      await markAsWatched(
        movieId,
        movie.Title || movie.title || 'Unknown Movie',
        rating,
        review,
        isPrivate,
        movie.Poster !== 'N/A' ? movie.Poster : undefined,
        movie.Year || movie.year,
        visibility,
        watchedDate || undefined,
        watchedDatePrecision
      );
      console.log(`StandardMovieCard [${movie.Title}]: Marked as watched successfully`);
    } catch (error) {
      console.error(`StandardMovieCard [${movie.Title}]: Error marking as watched:`, error);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  // Handle delete rating and review from dialog
  const handleDeleteRatingReview = async () => {
    setShowMarkWatchedDialog(false);
    setIsOperationInProgress(true);

    try {
      await deleteRatingAndReview(movieId);
      console.log(`StandardMovieCard [${movie.Title}]: Rating and review deleted successfully`);
    } catch (error) {
      console.error(`StandardMovieCard [${movie.Title}]: Error deleting rating and review:`, error);
    } finally {
      setIsOperationInProgress(false);
    }
  };

  // Helper function to safely get Rotten Tomatoes rating from OMDb data
  const getRottenTomatoesRating = () => {
    try {
      const rtRating = movie.Ratings?.find(r => r.Source === 'Rotten Tomatoes');
      if (!rtRating?.Value || rtRating.Value === 'N/A') {
        return null;
      }
      return rtRating.Value;
    } catch (error) {
      console.warn('Error getting Rotten Tomatoes rating:', error);
      return null;
    }
  };

  // Helper function to format IMDB rating to 1 decimal point
  const getFormattedImdbRating = () => {
    try {
      const rating = movie.imdbRating;
      if (!rating || rating === 'N/A') {
        return '7.3'; // Default fallback
      }

      const numericRating = parseFloat(rating);
      if (isNaN(numericRating)) {
        return '7.3'; // Default fallback for invalid values
      }

      // Format to 1 decimal place
      return numericRating.toFixed(1);
    } catch (error) {
      console.warn('Error formatting IMDB rating:', error);
      return '7.3'; // Default fallback
    }
  };

  // Helper function to get properly formatted runtime from multiple sources
  const getFormattedRuntime = () => {
    try {
      // First, try the string Runtime field (from OMDb or converted TMDb)
      if (movie.Runtime && movie.Runtime !== 'N/A' && movie.Runtime.trim() !== '') {
        return movie.Runtime;
      }

      // Second, try the numeric runtime field (from TMDb) and convert it
      if (movie.runtime && typeof movie.runtime === 'number' && movie.runtime > 0) {
        const hours = Math.floor(movie.runtime / 60);
        const minutes = movie.runtime % 60;
        if (hours > 0) {
          return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
        } else {
          return `${minutes}m`;
        }
      }

      // Return null when no actual runtime data is available
      return null;
    } catch (error) {
      console.warn('Error formatting runtime:', error);
      return null; // Return null instead of fallback
    }
  };

  // Helper function to render enhanced Rotten Tomatoes icon
  // Privacy icon helper functions using centralized service
  const getVisibilityIcon = (visibility?: 'anyone' | 'only-me') => {
    const iconSize = 14;
    const iconColor = theme.palette.text.secondary;

    const IconComponent = visibility === 'only-me' ? Lock : Public;
    return <IconComponent sx={{ fontSize: iconSize, color: iconColor }} />;
  };

  const getVisibilityText = (visibility?: 'anyone' | 'only-me') => {
    return visibility === 'only-me' ? 'Only you can see this' : 'Everyone can see this';
  };

  const renderRottenTomatoesIcon = (rating: string, iconSize: number = 12) => {
    const percentage = parseInt(rating.replace('%', '') || '0');
    const isFresh = percentage >= 60;

    return (
      <svg width={iconSize} height={iconSize} viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="6" fill={isFresh ? "#F93208" : "#02902E"} />
        {isFresh ? (
          // Fresh tomato icon
          <path d="M3.117 8.27C2.84 7.99 2.84 7.54 3.117 7.263L4.38 6L3.117 4.737C2.84 4.46 2.84 4.01 3.117 3.733C3.394 3.456 3.844 3.456 4.121 3.733L5.384 5.996L6.647 4.733C6.924 4.456 7.374 4.456 7.651 4.733C7.928 5.01 7.928 5.46 7.651 5.737L6.388 7L7.651 8.263C7.928 8.54 7.928 8.99 7.651 9.267C7.374 9.544 6.924 9.544 6.647 9.267L5.384 8.004L4.121 9.267C3.844 9.544 3.394 9.544 3.117 9.267V8.27Z" fill="white" />
        ) : (
          // Rotten tomato icon  
          <path d="M3.117 8.27C2.84 7.99 2.84 7.54 3.117 7.263L4.38 6L3.117 4.737C2.84 4.46 2.84 4.01 3.117 3.733C3.394 3.456 3.844 3.456 4.121 3.733L5.384 5.996L6.647 4.733C6.924 4.456 7.374 4.456 7.651 4.733C7.928 5.01 7.928 5.46 7.651 5.737L6.388 7L7.651 8.263C7.928 8.54 7.928 8.99 7.651 9.267C7.374 9.544 6.924 9.544 6.647 9.267L5.384 8.004L4.121 9.267C3.844 9.544 3.394 9.544 3.117 9.267V8.27Z" fill="white" />
        )}
      </svg>
    );
  };

  const renderCollaboratorBadge = () => {
    // Only show if this is a collaborative list AND we have collaborators data
    if (!isCollaborativeList || !collaborators || collaborators.length === 0) return null;

    // Determine watched status
    // 🚀 FIX: Ensure current user is included if they have watched it
    const viewers = [...(watchedBy || [])];

    // Check if current user has watched this movie in ANY context (personal or collaborative)
    // actualIsWatched comes from getUserMovieData context which aggregates all sources
    if (actualIsWatched && user?.uid && !viewers.includes(user.uid)) {
      viewers.push(user.uid);
      console.log(`StandardMovieCard [${movie.Title}]: Added current user ${user.uid} to viewers list standard logic`, viewers);
    }

    const watchedCount = viewers.length;

    // Total collaborators including current user if they are in the list
    const totalCollaborators = collaborators.length;

    const allWatched = totalCollaborators > 0 && watchedCount >= totalCollaborators;
    const isDarkMode = theme.palette.mode === 'dark';
    const anyWatched = watchedCount > 0;

    // Don't show if nobody watched (optional, checking user requirement "atleast one collaborator hasn't watched (grey)" - implies we show it always? "show a count+ eye icon to indicate how many have watched")
    // "when atleast one collaborator hasn't watched, green color when everyone has watched"
    // Assuming show always if there are collaborators

    // Determine positioning based on variant and context menu presence
    const isGridOrPortrait = effectiveVariant === 'grid' || effectiveVariant === 'portrait';
    const bottomPos = isGridOrPortrait ? 6 : 8;
    // If context menu is shown, position to the left of it (margin + button width + gap)
    const rightPos = showContextMenu
      ? (isGridOrPortrait ? 34 : 40)
      : (isGridOrPortrait ? 6 : 8);

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: bottomPos,
          right: rightPos,
          zIndex: 2,
          backgroundColor: isDarkMode ? '#2C2C2E' : '#E5E5EA',
          color: allWatched ? (isDarkMode ? '#30DB5B' : '#248A3D') : (isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)'),
          padding: '0 8px',
          minWidth: isGridOrPortrait ? 20 : 24,
          height: isGridOrPortrait ? 20 : 24,
          borderRadius: 12, // Pill shape
          gap: 0.5,
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: isDarkMode ? '#3A3A3C' : '#D1D1D6',
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          setShowCollaboratorsWatchedDialog(true);
        }}
        title={`${watchedCount}/${totalCollaborators} watched`}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 600, fontFamily: font() }}>
          {watchedCount}
        </Typography>
        <EyeIcon size={12} />
      </Box>
    );
  };

  const renderStatusBadge = () => {
    return (
      <>
        {renderCollaboratorBadge()}
        {renderMainStatusBadge()}
      </>
    );
  };

  const renderMainStatusBadge = () => {
    if (!showStatusBadge) return null;

    const isDarkMode = theme.palette.mode === 'dark';

    // Always fetch fresh context data for accurate real-time status
    const freshContextData = getUserMovieData(movieId);
    const isInWatchlistFromContext = freshContextData?.isInWatchlist || false;
    const isWatchedFromContext = freshContextData?.isWatched || false;

    // 🚀 UPDATED: Check if movie is only in collaboration lists added by others
    const isOnlyInCollabByOthers = isInCollabWatchlistsByOthers && !isInWatchlistFromContext && !isInCollabWatchlistsByUser;

    // Debug logging removed for performance - uncomment when debugging badge issues
    // console.log(`StandardMovieCard [${movie.Title}]: Rendering badge:`, { movieId, isWatchedFromContext, isInWatchlistFromContext });

    // Priority: Watched status first, then watchlist status
    if (isWatchedFromContext) {
      return (
        <Box
          sx={{
            ...getMovieCardStatusBadgeStyles(),
            ...getStatusBadgeStyles('watched', isDarkMode),
          } as any}
          onClick={(e) => {
            e.stopPropagation();
            console.log(`StandardMovieCard [${movie.Title}]: Watched badge clicked`);
            handleWatchedToggle();
          }}
          title="Watched - Click to unmark"
        >
          <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
            <path d="M1 4.5L4 7.5L11 0.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Box>
      );
    }

    // 🚀 UPDATED: For movies added by current user in any watchlist, show watchlist badge (blue list icon)
    if (isInWatchlistFromContext || isInCollabWatchlistsByUser) {
      return (
        <Box
          sx={{
            ...getMovieCardStatusBadgeStyles(),
            ...getStatusBadgeStyles('watchlist', isDarkMode),
          } as any}
          onClick={(e) => {
            e.stopPropagation();
            console.log(`StandardMovieCard [${movie.Title}]: Watchlist badge clicked`);
            handleWatchlistBadgeClick();
          }}
          title="In Watchlist - Click to remove"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 3H10M2 6H10M2 9H7" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Box>
      );
    }

    // 🚀 UPDATED: For movies only in collaboration lists added by others, show add to watchlist badge
    // This allows users to add movies from collaboration lists to their personal watchlist
    return (
      <Box
        sx={{
          ...getMovieCardStatusBadgeStyles(),
          ...getStatusBadgeStyles('addToWatchlist', isDarkMode),
        } as any}
        onClick={(e) => {
          e.stopPropagation();
          console.log(`StandardMovieCard [${movie.Title}]: Add to watchlist badge clicked (${isOnlyInCollabByOthers ? 'from collab list' : 'new movie'})`);
          handleWatchlistBadgeClick();
        }}
        title={isOnlyInCollabByOthers ? "Add to your watchlist" : "Add to Watchlist"}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3.333V12.667M3.333 8H12.667" stroke={isDarkMode ? '#FFFFFF' : '#5856D6'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Box>
    );
  };

  const renderConfirmationDialogs = () => (
    <>
      {/* Remove from Watchlist Confirmation Dialog */}
      <Dialog
        open={showWatchlistRemoveDialog}
        onClose={() => setShowWatchlistRemoveDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 18,
          fontFamily: font(),
          pb: 1
        }}>
          Remove from Watchlist?
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body2" sx={{
            color: theme.palette.text.secondary,
            fontFamily: font(),
            lineHeight: 1.5
          }}>
            Are you sure you want to remove "{movie.Title}" from your watchlist?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <CustomButton
            onClick={() => setShowWatchlistRemoveDialog(false)}
            semantic="secondary"
            label="Cancel"
            size="small"
            sx={{ minWidth: 80 }}
          />
          <CustomButton
            onClick={confirmRemoveFromWatchlist}
            semantic="error"
            label="Remove"
            size="small"
            loading={isOperationInProgress}
            disabled={isOperationInProgress}
            sx={{ minWidth: 80 }}
          />
        </DialogActions>
      </Dialog>

      {/* Unwatch Confirmation Dialog */}
      <Dialog
        open={showUnwatchDialog}
        onClose={() => setShowUnwatchDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 18,
          fontFamily: font(),
          pb: 1
        }}>
          Mark as Not Watched?
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body2" sx={{
            color: theme.palette.text.secondary,
            fontFamily: font(),
            lineHeight: 1.5
          }}>
            Are you sure you want to mark "{movie.Title}" as not watched?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <CustomButton
            onClick={() => setShowUnwatchDialog(false)}
            semantic="secondary"
            label="Cancel"
            size="small"
            sx={{ minWidth: 80 }}
          />
          <CustomButton
            onClick={confirmUnwatch}
            semantic="warning"
            label="Unwatch"
            size="small"
            loading={isOperationInProgress}
            disabled={isOperationInProgress}
            sx={{ minWidth: 80 }}
          />
        </DialogActions>
      </Dialog>

      {/* Mark as Watched Dialog */}
      <MarkAsWatchedDialog
        open={showMarkWatchedDialog}
        onClose={() => setShowMarkWatchedDialog(false)}
        movie={movie}
        user={user}
        existingRating={existingRating}
        existingReview={existingReview}
        existingWatchedDate={existingWatchedDate}
        onMarkAsWatched={handleMarkAsWatchedSubmit}
        onDeleteRatingReview={handleDeleteRatingReview}
      />

      {/* Watchlist Multi-Select Dialog */}
      <WatchlistMultiSelectDialog
        open={showWatchlistMultiSelectDialog}
        onClose={() => setShowWatchlistMultiSelectDialog(false)}
        movieId={movieId}
        movieTitle={movie.Title || movie.title || 'Unknown Movie'}
        moviePoster={movie.Poster !== 'N/A' ? movie.Poster : undefined}
        movieYear={movie.Year || movie.year}
        user={user}
        isWatched={actualIsWatched} // Pass watched status to hide "Want to Watch" for watched movies
        onSave={() => {
          setShowWatchlistMultiSelectDialog(false);
        }}
      />

      {/* Movie Note Dialog */}
      <MovieNoteDialog
        open={showMovieNoteDialog}
        onClose={() => setShowMovieNoteDialog(false)}
        onSave={handleSaveNote}
        movieTitle={movie.Title || movie.title || 'Unknown Movie'}
        moviePoster={movie.Poster !== 'N/A' ? movie.Poster : undefined}
        currentNote={movieNote || ''}
        isLoading={isOperationInProgress}
      />

      {/* Remove Note Confirmation Dialog */}
      <Dialog
        open={showRemoveNoteDialog}
        onClose={() => setShowRemoveNoteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          fontWeight: 600,
          fontSize: 18,
          fontFamily: font(),
          pb: 1
        }}>
          Remove Note?
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body2" sx={{
            color: theme.palette.text.secondary,
            fontFamily: font(),
            lineHeight: 1.5
          }}>
            Are you sure you want to remove the note for "{movie.Title}"?
          </Typography>
          {movieNote && movieNote.trim() && (
            <Box sx={{
              mt: 2,
              p: 1.5,
              bgcolor: theme.palette.mode === 'dark' ? '#353537' : '#F0F0F3',
              borderRadius: '4px',
              borderLeft: `3px solid ${theme.palette.mode === 'dark' ? '#5856D6' : '#5856D6'}`,
              textAlign: 'left'
            }}>
              <Typography sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: theme.palette.text.primary,
                fontFamily: font(),
                fontWeight: 400,
                fontStyle: 'italic'
              }}>
                "{movieNote}"
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 2 }}>
          <CustomButton
            onClick={() => setShowRemoveNoteDialog(false)}
            semantic="secondary"
            label="Cancel"
            size="small"
            sx={{ minWidth: 80 }}
          />
          <CustomButton
            onClick={confirmRemoveNote}
            semantic="error"
            label="Remove"
            size="small"
            loading={isOperationInProgress}
            disabled={isOperationInProgress}
            sx={{ minWidth: 80 }}
          />
        </DialogActions>
      </Dialog>

      {/* Collaborators Watched Dialog */}
      <CollaboratorsWatchedDialog
        open={showCollaboratorsWatchedDialog}
        onClose={() => setShowCollaboratorsWatchedDialog(false)}
        movieTitle={movie.Title || movie.title || 'Unknown Movie'}
        collaborators={collaborators || []}
        watchedBy={(() => {
          // 🚀 FIX: Ensure current user is included if they have watched it
          const viewers = [...(watchedBy || [])];
          if (actualIsWatched && user?.uid && !viewers.includes(user.uid)) {
            viewers.push(user.uid);
          }
          return viewers;
        })()}
        watchedDates={(() => {
          const dates: Record<string, any> = {};
          // Populate current user's date provided via context
          if (actualIsWatched && user?.uid && contextData?.watchedDate) {
            dates[user.uid] = contextData.watchedDate;
          }
          return dates;
        })()}
      />
    </>
  );

  if (effectiveVariant === 'poster-only') {
    return (
      <>
        <Box
          data-movie-id={movieId}
          sx={{
            position: 'relative',
            overflow: 'visible',
            cursor: 'pointer',
            '&:hover': {
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
            // Highlight animation when newly added
            ...(isHighlighted && {
              animation: `${highlightPulse} 1.5s ease-in-out`,
              zIndex: 10,
            }),
          }}
          onClick={handleOpenMovieClick}
          onPointerEnter={handlePosterPreload}
          onTouchStart={handlePosterPreload}
        >
          <Box sx={{
            width: posterSize.width,
            height: posterSize.height,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0
          }}>
            {movie.Poster && movie.Poster !== 'N/A' ? (
              <Box
                component="img"
                src={tmdbService.getGridPoster(movie.Poster)}
                alt={movie.Title}
                loading="lazy"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
              <Box sx={{
                width: '100%',
                height: '100%',
                bgcolor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography variant="caption"></Typography>
              </Box>
            )}
          </Box>
          {renderStatusBadge()}
        </Box>
        {renderConfirmationDialogs()}
      </>
    );
  }

  if (effectiveVariant === 'grid') {
    const isDarkMode = theme.palette.mode === 'dark';

    return (
      <>
        <Box
          data-movie-id={movieId}
          sx={{
            ...getMovieCardStyles('grid', isDarkMode),
            position: 'relative', // Needed for absolute positioning of the 3-dot menu
            // Ensure proper spacing on smaller screens
            '@media (max-width: 400px)': {
              p: 1,
              maxWidth: 160,
            },
            // Highlight animation when newly added
            ...(isHighlighted && {
              animation: `${highlightPulse} 1.5s ease-in-out`,
              zIndex: 10,
            }),
          }}
          onClick={handleOpenMovieClick}
          onPointerEnter={handlePosterPreload}
          onTouchStart={handlePosterPreload}
        >
          {/* Movie Poster */}
          <Box sx={{
            ...getMovieCardPosterStyles('grid', isDarkMode),
          }}>
            <ImageShimmer
              src={movie.Poster && movie.Poster !== 'N/A' ? tmdbService.getGridPoster(movie.Poster) : null}
              alt={movie.Title}
              width="100%"
              height="100%"
              objectFit="cover"
            />
            {renderStatusBadge()}
          </Box>

          {/* Movie Title */}
          <Typography sx={{
            fontWeight: 800,
            fontSize: '13px',
            lineHeight: 1.3,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            color: isDarkMode ? '#FFFFFF' : '#000000',
          }}>
            {movie.Title || movie.title}
            {movie.original_title && (movie.Title || movie.title) !== movie.original_title && movie.original_language !== 'en' && (
              <span style={{
                fontWeight: 500,
                opacity: 0.7,
                fontSize: '0.9em',
                marginLeft: '4px'
              }}>
                ({movie.original_title})
              </span>
            )}
          </Typography>

          {/* Movie Year */}
          <Typography variant="body2" sx={{
            color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
            fontSize: '12px',
            mb: 0.5,
            fontFamily: font(),
            lineHeight: 1.33
          }}>
            {movie.Year || movie.year || new Date().getFullYear().toString()} ‧ {movie.Genre && movie.Genre !== 'N/A' && movie.Genre.trim() ? movie.Genre.split(',')[0] : 'Drama'}{getFormattedRuntime() ? ` ‧ ${getFormattedRuntime()}` : ''}
          </Typography>

          {/* Search Match Reasons */}
          {matchedFields && matchedFields.length > 0 && (
            <Typography variant="body2" sx={{
              color: theme.palette.mode === 'dark' ? 'rgba(88, 86, 214, 0.9)' : 'rgba(88, 86, 214, 0.8)',
              fontSize: '11px',
              mb: 0.5,
              fontFamily: font(),
              lineHeight: 1.33,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(88, 86, 214, 0.9)' : 'rgba(88, 86, 214, 0.8)'
              }} />
              {getMatchReasonText(matchedFields)}
            </Typography>
          )}

          {/* Date Information */}
          {(addedOn || watchedOn) && (
            <Typography variant="body2" sx={{
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              fontSize: { xs: '10px', sm: '11px' },
              mb: 1,
              fontFamily: font(),
              lineHeight: 1.33
            }}>
              {actualIsWatched && watchedOn ?
                `Watched ${formatWatchedDate(watchedOn, contextData?.watchedDatePrecision)}` :
                `Added ${formatFirebaseDate(addedOn || '2024-01-01', 'Recently', { month: 'short', day: 'numeric' })}`
              }
            </Typography>
          )}

          {/* Your Rating - Show for watched movies without reviews */}
          {actualIsWatched && contextData?.rating && (!userReview || !userReview.review) && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1
            }}>
              <Box sx={{
                width: 14,
                height: 14,
                color: '#FF9500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="14" height="14" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22 20H20V18H18V16H20V14H22V12H24V14H26V16H28V18H26V20H24V22H22V20Z" fill="#FF9500" />
                  <path d="M8 20H6V18H4V16H6V14H8V12H10V14H12V16H14V18H12V20H10V22H8V20Z" fill="#FF9500" />
                </svg>
              </Box>
              <Typography variant="body2" sx={{
                fontWeight: 500,
                fontSize: 11,
                color: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(0, 0, 0, 0.9)',
                lineHeight: 1.33,
                fontFamily: font()
              }}>
                {contextData.rating}/5
              </Typography>
            </Box>
          )}


          {/* Added By - Show for collaborators in non-public lists */}
          {shouldShowAddedBy && (
            <Typography variant="caption" sx={{
              fontSize: 9,
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
              fontFamily: font(),
              lineHeight: 1.6,
              fontStyle: 'italic',
              mt: 0.5,
            }}>
              Added by {addedByName}
            </Typography>
          )}

          {/* 3-dot menu at the bottom right for grid variant - only show in watchlist contexts */}
          {showContextMenu && (
            <IconButton
              onClick={handleMenuClick}
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                width: 20,
                height: 20,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.8)',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.9)',
                },
                borderRadius: '50%',
              }}
            >
              <DotsThreeIcon size={14} color={theme.palette.mode === 'dark' ? 'white' : 'black'}  weight="bold" />
            </IconButton>
          )}
        </Box>

        {/* 3-dot menu for grid variant */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              ...glassStyles.menu(theme.palette.mode === 'dark'),
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                fontFamily: font(),
                py: 1,
                px: 2,
              }
            }
          }}
        >
          {!isWatched && (
            <MenuItem onClick={handleMarkAsWatched}>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><CheckIcon size={20} weight="bold" /></Box>
              Mark as watched
            </MenuItem>
          )}
          <MenuItem onClick={handleAddToAnotherList}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><ListPlusIcon size={20} /></Box>
            Add to another list
          </MenuItem>
          <MenuItem onClick={handleRemoveFromList}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><MinusIcon size={20} /></Box>
            Remove from list
          </MenuItem>
          <MenuItem onClick={handleAddEditNote}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><PencilSimpleIcon size={20} /></Box>
            {movieNote ? 'Edit note' : 'Add note'}
          </MenuItem>
          {movieNote && movieNote.trim() && (
            <MenuItem onClick={handleRemoveNote}>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><TrashIcon size={20} /></Box>
              Remove note
            </MenuItem>
          )}
        </Menu>

        {renderConfirmationDialogs()}
      </>
    );
  }

  // Watched with Review variant
  if (effectiveVariant === 'watched-review' && userReview) {
    return (
      <>
        <Box data-movie-id={movieId} sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <Card sx={{
            borderRadius: '12px 12px 12px 12px',
            bgcolor: theme.palette.mode === 'dark' ? 'rgb(34, 34, 36)' : 'rgba(0, 0, 0, 0.05)',
            boxShadow: 'none',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'visible',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? 'rgba(2, 144, 46, 0.2)'
                : 'rgba(2, 144, 46, 0.18)',
            },
            transition: 'all 0.2s ease-in-out',
            // Highlight animation when newly added
            ...(isHighlighted && {
              animation: `${highlightPulse} 1.5s ease-in-out`,
              zIndex: 10,
            }),
          }}
            onClick={handleOpenMovieClick}
            onPointerEnter={handlePosterPreload}
            onTouchStart={handlePosterPreload}
          >
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, mb: { xs: 1, sm: 1.5 } }}>
                {/* Movie Poster */}
                <Box sx={{
                  width: posterSize.width,
                  height: posterSize.height,
                  borderRadius: 1,
                  overflow: 'hidden',
                  flexShrink: 0,
                  bgcolor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#F0F0F0',
                }}>
                  <ImageShimmer
                    src={movie.Poster && movie.Poster !== 'N/A' ? tmdbService.getGridPoster(movie.Poster) : null}
                    alt={movie.Title}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                    borderRadius={2}
                  />
                </Box>

                {/* Movie Info */}
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        fontSize: { xs: '15px', sm: '16px' },
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                        hyphens: 'auto',
                        fontFamily: font(),
                        lineHeight: 1.4,
                      }}
                    >
                      {movie.Title}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                      fontSize: { xs: '12px', sm: '12px' },
                      mb: 0.5,
                      fontFamily: font(),
                      lineHeight: 1.33,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      wordBreak: 'break-word',
                    }}
                  >
                    {movie.Year || movie.year || new Date().getFullYear().toString()} ‧ {movie.Genre && movie.Genre !== 'N/A' && movie.Genre.trim() ? movie.Genre.split(',')[0] : 'Drama'}{getFormattedRuntime() ? ` ‧ ${getFormattedRuntime()}` : ''}
                  </Typography>

                  {/* Date Information */}
                  {(addedOn || watchedOn) && (
                    <Typography variant="body2" sx={{
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                      fontSize: { xs: '10px', sm: '11px' },
                      mb: 1,
                      fontFamily: font(),
                      lineHeight: 1.33
                    }}>
                      {actualIsWatched && watchedOn ?
                        `Watched on ${formatWatchedDate(watchedOn, contextData?.watchedDatePrecision)}` :
                        `Added on ${formatFirebaseDate(addedOn || '2024-01-01', 'Recently', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      }
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                    <Typography sx={{
                      fontSize: { xs: 10, sm: 11 },
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.6)'
                        : 'rgba(0, 0, 0, 0.6)',
                      fontFamily: font(),
                      lineHeight: 1.33,
                    }}>
                      {userReview
                        ? `Updated ${userReviewUpdatedLabel || 'recently'}${userReviewWatchedLabel ? ` · Watched ${userReviewWatchedLabel}` : ''}`
                        : formatWatchedDate(contextData?.watchedDate, contextData?.watchedDatePrecision)
                    }
                    </Typography>

                    {/* Privacy Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                      {getVisibilityIcon(contextData?.reviewVisibility)}
                    </Box>
                  </Box>

                  {/* Movie Ratings for watched-review variant */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                    {/* TMDb Rating */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <img src="/assets/Icons/tmdb_logo.svg" alt="TMDb" style={{ height: 6, width: 'auto' }} />
                      <Typography variant="caption" sx={{
                        fontSize: 10,
                        color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                        fontFamily: font(),
                        lineHeight: 1.6
                      }}>
                        {getFormattedImdbRating()}
                      </Typography>
                    </Box>



                    {/* Watchlist Average Rating - Only show if available */}
                    {averageUserRating !== null && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{
                          width: 16,
                          height: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="16" height="16" viewBox="0 0 72 72" fill="none">
                            <path d="M49 44H44V39H39V34H44V29H49V24H54V29H59V34H64V39H59V44H54V49H49V44Z" fill={theme.palette.mode === 'dark' ? '#E7E1F3' : '#232127'} />
                            <path d="M19 44H14V39H9V34H14V29H19V24H24V29H29V34H34V39H29V44H24V49H19V44Z" fill={theme.palette.mode === 'dark' ? '#E7E1F3' : '#232127'} />
                          </svg>
                        </Box>
                        <Typography variant="caption" sx={{
                          fontSize: 10,
                          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontFamily: font(),
                          lineHeight: 1.6
                        }}>
                          {averageUserRating.toFixed(1)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Added By - Show for collaborators in non-public lists */}
                  {shouldShowAddedBy && (
                    <Typography variant="caption" sx={{
                      fontSize: 10,
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                      fontFamily: font(),
                      lineHeight: 1.6,
                      fontStyle: 'italic',
                      mt: 0.5,
                    }}>
                      Added by {addedByName}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Your Rating - Show just above the review */}
              {userReview?.rating && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: { xs: 0, sm: 0 }
                }}>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    color: '#FF9500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M8 1.33333L9.86667 5.86667L15.3333 6.66667L11.6667 10.2L12.7333 15.6667L8 13.2L3.26667 15.6667L4.33333 10.2L0.666668 6.66667L6.13333 5.86667L8 1.33333Z" fill="#FF9500" />
                    </svg>
                  </Box>
                  <Typography variant="body2" sx={{
                    fontWeight: 500,
                    fontSize: { xs: 12, sm: 14 },
                    color: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(0, 0, 0, 0.9)',
                    lineHeight: 1.33,
                    fontFamily: font()
                  }}>
                    Your rating: {userReview.rating}/5
                  </Typography>
                  {userReview.review?.trim() && (
                    <PencilLineIcon size={14} color={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)'} />
                  )}
                </Box>
              )}
            </CardContent>

            {renderStatusBadge()}

            {/* 3-dot menu at the bottom right for watched-review variant - only show in watchlist contexts */}
            {showContextMenu && (
              <IconButton
                onClick={handleMenuClick}
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.9)',
                  },
                  borderRadius: '50%',
                }}
              >
                <DotsThreeIcon size={16} color={theme.palette.mode === 'dark' ? 'white' : 'black'}  weight="bold" />
              </IconButton>
            )}
          </Card>

          {/* Movie Note - Now displayed below the watched-review card */}
          {movieNote && movieNote.trim() && (
            <Box sx={{
              mt: -0.5,
              px: 1.5,
              py: 1.5,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: '0px 0px 12px 12px',
            }}>
              <Typography sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: theme.palette.text.primary,
                fontFamily: font(),
                fontWeight: 400,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
              }}>
                {movieNote}
              </Typography>
            </Box>
          )}
        </Box>

        {/* 3-dot menu for watched-review variant */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              ...glassStyles.menu(theme.palette.mode === 'dark'),
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                fontFamily: font(),
                py: 1,
                px: 2,
              }
            }
          }}
        >
          {!isWatched && (
            <MenuItem onClick={handleMarkAsWatched}>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><CheckIcon size={20} weight="bold" /></Box>
              Mark as watched
            </MenuItem>
          )}
          <MenuItem onClick={handleAddToAnotherList}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><ListPlusIcon size={20} /></Box>
            Add to another list
          </MenuItem>
          <MenuItem onClick={handleRemoveFromList}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><MinusIcon size={20} /></Box>
            Remove from list
          </MenuItem>
          <MenuItem onClick={handleAddEditNote}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><PencilLineIcon size={20} /></Box>
            {movieNote ? 'Edit note' : 'Add note'}
          </MenuItem>
          {movieNote && movieNote.trim() && (
            <MenuItem onClick={handleRemoveNote}>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><TrashIcon size={20} /></Box>
              Remove note
            </MenuItem>
          )}
        </Menu>

        {renderConfirmationDialogs()}
      </>
    );
  }

  if (effectiveVariant === 'portrait') {
    return (
      <>
        <Box
          data-movie-id={movieId}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            width: { xs: 120, sm: 140, md: 160 },
            position: 'relative',
            '&:hover': {
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
            // Highlight animation when newly added
            ...(isHighlighted && {
              animation: `${highlightPulse} 1.5s ease-in-out`,
              zIndex: 10,
            }),
          }}
          onClick={handleOpenMovieClick}
          onPointerEnter={handlePosterPreload}
          onTouchStart={handlePosterPreload}
        >
          {/* Movie Poster */}
          <Box sx={{
            width: '100%',
            aspectRatio: '2/3',
            borderRadius: 2,
            overflow: 'hidden',
            mb: 1,
            position: 'relative',
            bgcolor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#F0F0F0',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 4px 12px rgba(0,0,0,0.4)'
              : '0 4px 12px rgba(0,0,0,0.1)',
          }}>
            <ImageShimmer
              src={movie.Poster && movie.Poster !== 'N/A' ? tmdbService.getHighQualityPoster(movie.Poster) : null}
              alt={movie.Title}
              width="100%"
              height="100%"
              objectFit="cover"
              borderRadius={2}
            />
            {/* Badge positioned on right top */}
            {showStatusBadge && (
              <Box sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 2,
              }}>
                {renderStatusBadge()}
              </Box>
            )}
          </Box>

          {/* Movie Title */}
          <Typography variant="body1" sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
            fontSize: { xs: '13px', sm: '14px' },
            mb: 0.5,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            textOverflow: 'ellipsis',
            fontFamily: font(),
            lineHeight: 1.4,
            minHeight: { xs: '32px', sm: '36px' }, // Ensure consistent height for 2 lines
          }}>
            {movie.Title}
          </Typography>

          {/* Movie Year */}
          <Typography variant="body2" sx={{
            color: theme.palette.text.secondary,
            fontSize: { xs: '11px', sm: '12px' },
            fontFamily: font(),
            lineHeight: 1.33,
            opacity: 0.8,
          }}>
            {movie.Year || movie.year || new Date().getFullYear().toString()}
          </Typography>

          {/* 3-dot menu at the bottom right for portrait variant - only show in watchlist contexts */}
          {showContextMenu && (
            <IconButton
              onClick={handleMenuClick}
              sx={{
                position: 'absolute',
                  bottom: 4,
                  right: 4,
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.9)',
                  },
                borderRadius: '50%',
              }}
            >
              <DotsThreeIcon size={14} color={theme.palette.mode === 'dark' ? 'white' : 'black'}  weight="bold" />
            </IconButton>
          )}
        </Box>

        {/* 3-dot menu for portrait variant */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              ...glassStyles.menu(theme.palette.mode === 'dark'),
              mt: 0.5,
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                fontFamily: font(),
                py: 1,
                px: 2,
              }
            }
          }}
        >
          {!isWatched && (
            <MenuItem onClick={handleMarkAsWatched}>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><CheckIcon size={20} weight="bold" /></Box>
              Mark as watched
            </MenuItem>
          )}
          <MenuItem onClick={handleAddToAnotherList}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><ListPlusIcon size={20} /></Box>
            Add to another list
          </MenuItem>
          <MenuItem onClick={handleRemoveFromList}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><MinusIcon size={20} /></Box>
            Remove from list
          </MenuItem>
          <MenuItem onClick={handleAddEditNote}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><PencilSimpleIcon size={20} /></Box>
            {movieNote ? 'Edit note' : 'Add note'}
          </MenuItem>
          {movieNote && movieNote.trim() && (
            <MenuItem onClick={handleRemoveNote}>
              <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><TrashIcon size={20} /></Box>
              Remove note
            </MenuItem>
          )}
        </Menu>

        {renderConfirmationDialogs()}
      </>
    );
  }

  // Default list variant
  return (
    <>
      <Box data-movie-id={movieId} sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start', // Changed from 'center' to 'flex-start' for better text alignment
            gap: { xs: 1.5, sm: 2 },
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            cursor: 'pointer',
            bgcolor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F0EFF0',
            position: 'relative', // ✅ Ensure badges are positioned relative to this container
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#E5E5EA',
            },
            transition: 'all 0.2s ease-in-out',
            minHeight: { xs: 100, sm: 110 },
            maxWidth: '100%',
            overflow: 'hidden',
            '@media (max-width: 400px)': {
              p: 1,
              gap: 1,
            },
            // Highlight animation when newly added
            ...(isHighlighted && {
              animation: `${highlightPulse} 1.5s ease-in-out`,
              zIndex: 10,
            }),
          }}
          onClick={handleOpenMovieClick}
          onPointerEnter={handlePosterPreload}
          onTouchStart={handlePosterPreload}
        >
          {/* Movie Poster */}
          <Box sx={{
            width: posterSize.width,
            height: posterSize.height,
            borderRadius: 1,
            overflow: 'hidden',
            flexShrink: 0,
            bgcolor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#F0F0F0',
          }}>
            <ImageShimmer
              src={movie.Poster && movie.Poster !== 'N/A' ? tmdbService.getHighQualityPoster(movie.Poster) : null}
              alt={movie.Title}
              width="100%"
              height="100%"
              objectFit="cover"
              borderRadius={1}
            />
          </Box>

          {/* Movie Info */}
          <Box sx={{ flex: 1, minWidth: 0, py: 0.5, display: 'flex', flexDirection: 'column' }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 400,
                color: theme.palette.text.primary,
                fontSize: { xs: '15px', sm: '16px' },
                mb: 0.25,
                fontFamily: font(),
                lineHeight: 1.4,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
                hyphens: 'auto',
              }}
            >
              {movie.Title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                fontSize: { xs: '12px', sm: '12px' },
                mb: 0.5,
                fontFamily: font(),
                lineHeight: 1.33,
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
            >
              {movie.Year || movie.year || new Date().getFullYear().toString()} ‧ {movie.Genre && movie.Genre !== 'N/A' && movie.Genre.trim() ? movie.Genre.split(',')[0] : 'Drama'}{getFormattedRuntime() ? ` ‧ ${getFormattedRuntime()}` : ''}
            </Typography>



            {/* Date Information */}
            {(addedOn || watchedOn) && (
              <Typography variant="body2" sx={{
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                fontSize: { xs: '10px', sm: '11px' },
                mb: 1,
                fontFamily: font(),
                lineHeight: 1.33
              }}>
              {actualIsWatched && watchedOn ?
                `Watched on ${formatWatchedDate(watchedOn, contextData?.watchedDatePrecision)}` :
                `Added on ${formatFirebaseDate(addedOn || '2024-01-01', 'Recently', { month: 'short', day: 'numeric', year: 'numeric' })}`
              }
              </Typography>
            )}

            {/* Your Rating - Show for watched movies without reviews */}
            {actualIsWatched && contextData?.rating && (!userReview || !userReview.review) && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1
              }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  color: '#FF9500',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 1.33333L9.86667 5.86667L15.3333 6.66667L11.6667 10.2L12.7333 15.6667L8 13.2L3.26667 15.6667L4.33333 10.2L0.666668 6.66667L6.13333 5.86667L8 1.33333Z" fill="#FF9500" />
                  </svg>
                </Box>
                <Typography variant="body2" sx={{
                  fontWeight: 500,
                  fontSize: { xs: 12, sm: 14 },
                  color: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.9)'
                    : 'rgba(0, 0, 0, 0.9)',
                  lineHeight: 1.33,
                  fontFamily: font()
                }}>
                  Your rating: {contextData.rating}/10
                </Typography>
              </Box>
            )}

            {/* Ratings */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mt: 'auto' }}>
              {/* Rotten Tomatoes - Only show if available from OMDb */}
              {getRottenTomatoesRating() && (
                <Tooltip title={`Rotten Tomatoes: ${getRottenTomatoesRating()} ${parseInt(getRottenTomatoesRating()?.replace('%', '') || '0') >= 60 ? 'Fresh' : 'Rotten'}`} arrow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
                    <Box sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}>
                      {renderRottenTomatoesIcon(getRottenTomatoesRating() || '0')}
                    </Box>
                    <Typography variant="caption" sx={{
                      fontSize: 10,
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                      fontFamily: font(),
                      lineHeight: 1.6,
                      fontWeight: 500
                    }}>
                      {getRottenTomatoesRating()}
                    </Typography>
                  </Box>
                </Tooltip>
              )}

              {/* TMDb */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <img src="/assets/Icons/tmdb_logo.svg" alt="TMDb" style={{ height: 6, width: 'auto' }} />
                <Typography variant="caption" sx={{
                  fontSize: 10,
                  color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                  fontFamily: font(),
                  lineHeight: 1.6
                }}>
                  {getFormattedImdbRating()}
                </Typography>
              </Box>

              {/* Watchlist Average Rating - Only show if available */}
              {averageUserRating !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <svg width="16" height="16" viewBox="0 0 72 72" fill="none">
                      <path d="M49 44H44V39H39V34H44V29H49V24H54V29H59V34H64V39H59V44H54V49H49V44Z" fill={theme.palette.mode === 'dark' ? '#E7E1F3' : '#232127'} />
                      <path d="M19 44H14V39H9V34H14V29H19V24H24V29H29V34H34V39H29V44H24V49H19V44Z" fill={theme.palette.mode === 'dark' ? '#E7E1F3' : '#232127'} />
                    </svg>
                  </Box>
                  <Typography variant="caption" sx={{
                    fontSize: 10,
                    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                    fontFamily: font(),
                    lineHeight: 1.6
                  }}>
                    {averageUserRating.toFixed(1)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Added By - Show for collaborators in non-public lists */}
            {shouldShowAddedBy && (
              <Typography variant="caption" sx={{
                fontSize: 10,
                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                fontFamily: font(),
                lineHeight: 1.6,
                fontStyle: 'italic',
                mt: 0.5,
              }}>
                Added by {addedByName}
              </Typography>
            )}
          </Box>

          {/* Status Badge - Positioned within the card container */}
          {renderStatusBadge()}

          {/* 3-dot menu at the bottom right - only show in watchlist contexts */}
          {showContextMenu && (
            <IconButton
              onClick={handleMenuClick}
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                width: 32,
                height: 32,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.8)',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.9)',
                },
                borderRadius: '50%',
              }}
            >
              <DotsThreeIcon size={16} color={theme.palette.mode === 'dark' ? 'white' : 'black'}  weight="bold" />
            </IconButton>
          )}
        </Box>

        {/* Movie Note - Now displayed below the card */}
        {movieNote && movieNote.trim() && (
          <Box sx={{
            mt: -0.5,
            px: 1.5,
            py: 1.5,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.05)',
            borderRadius: '0px 0px 12px 12px',
          }}>
            <Typography sx={{
              fontSize: 12,
              lineHeight: 1.5,
              color: theme.palette.text.primary,
              fontFamily: font(),
              fontWeight: 400,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}>
              {movieNote}
            </Typography>
          </Box>
        )}
      </Box>

      {/* 3-dot menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            ...glassStyles.menu(theme.palette.mode === 'dark'),
            mt: 0.5,
            '& .MuiMenuItem-root': {
              fontSize: '14px',
              fontFamily: font(),
              py: 1,
              px: 2,
            }
          }
        }}
      >
        {!isWatched && (
          <MenuItem onClick={handleMarkAsWatched}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><CheckIcon size={20} weight="bold" /></Box>
            Mark as watched
          </MenuItem>
        )}
        <MenuItem onClick={handleAddToAnotherList}>
          <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><ListPlusIcon size={20} /></Box>
          Add to another list
        </MenuItem>
        <MenuItem onClick={handleRemoveFromList}>
          <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><MinusIcon size={20} /></Box>
          Remove from list
        </MenuItem>
        <MenuItem onClick={handleAddEditNote}>
          <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><PencilSimpleIcon size={20} /></Box>
          {movieNote ? 'Edit note' : 'Add note'}
        </MenuItem>
        {movieNote && movieNote.trim() && (
          <MenuItem onClick={handleRemoveNote}>
            <Box component="span" sx={{ mr: 1.5, display: 'flex' }}><TrashIcon size={20} /></Box>
            Remove note
          </MenuItem>
        )}
      </Menu>

      {renderConfirmationDialogs()}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders during scroll
  // Only re-render if meaningful props have changed

  const movieChanged = (
    prevProps.movie.id !== nextProps.movie.id ||
    prevProps.movie.imdbID !== nextProps.movie.imdbID ||
    prevProps.movie.Title !== nextProps.movie.Title ||
    prevProps.movie.Poster !== nextProps.movie.Poster ||
    prevProps.movie.Year !== nextProps.movie.Year
  );

  const statusChanged = (
    prevProps.isInWatchlist !== nextProps.isInWatchlist ||
    prevProps.isWatched !== nextProps.isWatched ||
    prevProps.movieNote !== nextProps.movieNote
  );

  const configChanged = (
    prevProps.variant !== nextProps.variant ||
    prevProps.size !== nextProps.size ||
    prevProps.showStatusBadge !== nextProps.showStatusBadge ||
    prevProps.showContextMenu !== nextProps.showContextMenu ||
    prevProps.currentWatchlistId !== nextProps.currentWatchlistId
  );

  const collaborationChanged = (
    prevProps.addedByName !== nextProps.addedByName ||
    prevProps.isCollaborativeList !== nextProps.isCollaborativeList ||
    prevProps.isPublicList !== nextProps.isPublicList ||
    prevProps.currentUserIsCollaborator !== nextProps.currentUserIsCollaborator
  );

  const userChanged = (
    prevProps.user?.uid !== nextProps.user?.uid
  );

  // Return true if props are equal (no re-render needed)
  return !movieChanged && !statusChanged && !configChanged && !collaborationChanged && !userChanged;
});

export default StandardMovieCard; 
